import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import mergeClasses from '../../../utils/shallowMerge';
import { Cross } from '../../atoms/icons';
import defaultClasses from './scss/Tooltip.module.scss';
import { useDimensions } from './useTooltip';
import { POSITIONS } from './utils/const';
import {
  FOCUSABLE_SELECTOR,
  calculateTooltipAlign,
  calculateTooltipPosition,
  getAlign,
  getPosition,
  resolveContainerAlign,
  resolveContainerPosition,
} from './utils/helper';

const TooltipContent = ({
  shown,
  tooltipId,
  children,
  onClose,
  onCloseMobile,
  preferredPosition,
  preferredAlign,
  containerRef,
  parent,
  customClasses,
}) => {
  const styles = mergeClasses(defaultClasses, customClasses);
  const tooltip = useRef(null);
  const content = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (tooltip.current && !tooltip.current.contains(event.target)) {
        onClose();
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [tooltip]);

  const dimensions = useDimensions(
    { containerRef, tooltip, content },
    children,
    parent,
  );

  const positions = getPosition(POSITIONS, preferredPosition);

  const aligns = getAlign(preferredAlign);

  const position = useMemo(
    () => calculateTooltipPosition(positions, dimensions),
    [dimensions, positions],
  );

  const align = useMemo(
    () => calculateTooltipAlign(position, aligns, dimensions),
    [aligns, dimensions, position],
  );

  const handleInnerClick = useCallback(
    (ev) => {
      if (tooltip.current) {
        const focusableElements =
          tooltip.current.querySelectorAll(FOCUSABLE_SELECTOR);
        if (Object.values(focusableElements).some((v) => v === ev.target)) {
          onClose();
          // onCloseMobile();
        }
      }
    },
    [onClose, onCloseMobile],
  );

  const tooltipWrapperClasses = clsx(styles.tooltipWrapper, {
    [` ${styles.tooltipWrapper__shown}`]: shown && position && align,
  });

  const arrowClasses = clsx(
    styles.arrow,
    styles[`arrow__${position}`],
    styles[`arrow__${position}__${align}`],
  );

  const tooltipParams = {
    align,
    position,
    containerTop: dimensions.containerTop,
    containerLeft: dimensions.containerLeft,
    containerHeight: dimensions.containerHeight,
    containerWidth: dimensions.containerWidth,
    tooltipHeight: dimensions.tooltipHeight,
    contentHeight: dimensions.contentHeight,
    tooltipWidth: dimensions.tooltipWidth,
    containerLeftPure: dimensions.containerLeftPure,
    containerTopPure: dimensions.containerTopPure,
  };

  return (
    <div role="tooltip" id={tooltipId} className={styles.tooltip}>
      <div
        className={tooltipWrapperClasses}
        style={{
          ...resolveContainerPosition(tooltipParams),
          ...resolveContainerAlign(tooltipParams),
        }}
        ref={tooltip}
        role="tooltip"
        aria-hidden={!shown}
        onClick={handleInnerClick}
      >
        <div className={styles.tooltipContainer}>
          <div ref={content} className={styles.content}>
            {children}
          </div>
          <button
            className={styles.buttonClose}
            onClick={onClose}
            type="button"
          >
            <Cross />
          </button>
        </div>
        <span className={arrowClasses} />
      </div>
    </div>
  );
};

TooltipContent.propTypes = {
  shown: PropTypes.bool,
  tooltipId: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  onClose: PropTypes.func,
  onCloseMobile: PropTypes.func,
  onEnter: PropTypes.func,
  preferredPosition: PropTypes.string,
  preferredAlign: PropTypes.string,
  containerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.object }),
  ]),
  parent: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.object }),
  ]),
  customClasses: PropTypes.shape({
    tooltipWrapper: PropTypes.string,
    tooltipWrapper__shown: PropTypes.string,
    arrow: PropTypes.string,
    arrow__left: PropTypes.string,
    arrow__right: PropTypes.string,
    arrow__top: PropTypes.string,
    arrow__bottom: PropTypes.string,
    arrow__left__start: PropTypes.string,
    arrow__left__end: PropTypes.string,
    arrow__left__center: PropTypes.string,
    arrow__right__start: PropTypes.string,
    arrow__right__end: PropTypes.string,
    arrow__right__center: PropTypes.string,
    arrow__top__start: PropTypes.string,
    arrow__top__end: PropTypes.string,
    arrow__top__center: PropTypes.string,
    arrow__bottom__start: PropTypes.string,
    arrow__bottom__end: PropTypes.string,
    arrow__bottom__center: PropTypes.string,
  }),
};

TooltipContent.defaultProps = {
  shown: false,
  tooltipId: null,
  onClose: null,
  onCloseMobile: null,
  onEnter: null,
  preferredPosition: null,
  preferredAlign: 'center',
  containerRef: null,
  parent: null,
  customClasses: null,
};

export default TooltipContent;
